import { mobileWidth, tabletWidth } from "../../utilis/screenWidthUtils";
import {PhoneHQButton, PhoneHQGlyph} from 'ch3-ui-lib';
import { Link } from "react-router-dom";
import google from '../../assets/google.svg';
import pipedrive from '../../assets/pipedrive.svg';
import webhook from '../../assets/webhooks.svg';
import {
    useGetIntegrationsQuery, useGetWebhooksQuery, useGetWebhookConfigurationQuery
} from "../../store/features/integrations/integrationsService";
import AdminPanelRestrictedView from "../../components/AdminPanelRestrictedView/AdminPanelRestrictedView";
import {useSelector} from "react-redux";
import {DisconnectIntegrationDialog} from "./DisconnectIntegration.tsx";
import {useState} from "react";
import {useIsChatWebhooksEnabledQuery} from "../../services/chatService.ts";
import apiIcon from '../../assets/api-icon.svg'

const integrations = [
    {
        id: 'GOOGLE_WORKSPACE',
        name: "Google Workspace",
        description: "Sync your Google Workspace user with PhoneHQ",
        image: google,
        link: "/admin/integrations/google",
        integratedLink: "/admin/integrations/google/list"
    },
    {
        id: 'PIPEDRIVE',
        name: "Pipedrive",
        description: "Sync contacts and sales to boost team efficiency. Easily connect PhoneHQ with Pipedrive.",
        image: pipedrive,
        link: "/admin/integrations/pipedrive",
        // This will be dynamically replaced with the actual integration ID
        integratedLink: "/admin/integrations/pipedrive/settings"
    },
    {
        id: 'WEBHOOK',
        name: "Outgoing webhooks",
        description: "Transfer data from PhoneHQ to other apps in real-time. Set up custom webhook connections.",
        image: webhook,
        link: "/admin/webhook/list",
        // This will be dynamically replaced with the actual integration ID
        integratedLink: "/admin/webhook/list",
        label: 'View integration'
    },
    {
        id: 'INCOMING_CHAT_WEBHOOK',
        name: "Incoming chat webhooks",
        description: "Post messages from Grafana, CI pipelines and automation tools into your chat rooms.",
        image: webhook,
        link: "/admin/integrations/chat-webhooks",
        integratedLink: "/admin/integrations/chat-webhooks",
        label: 'View integration'
    },
    {
        id: 'API',
        name: "API",
        description: "Use our API to manipulate data from PhoneHQ in the way that best suits your needs.",
        image:  apiIcon,
        link: "/admin/api/list",
        // This will be dynamically replaced with the actual integration ID
        integratedLink: "/admin/api/list",
        label: 'View integration'
    }

];

type Platform = 'GOOGLE_WORKSPACE' | 'PIPEDRIVE';

interface IntegrationCardProps {
    isEnabled: boolean;
    name: string;
    description: string;
    image: string;
    link: string;
    integratedLink: string;
    integrationId?: number;
    platform: string;
}

function IntegrationCard({ integrationId, isEnabled, name, description, image, link, integratedLink, platform }: IntegrationCardProps) {

    const {data: webhookData} = useGetWebhooksQuery({name: '', include: 'PHONE_NUMBERS'});
    const {data: webhookConfig} = useGetWebhookConfigurationQuery();
    const isIntegrationEnabled = () => {
        if (platform === 'GOOGLE_WORKSPACE') {
            return isEnabled;
        } else if (platform === 'PIPEDRIVE') {
            return isEnabled;
        } else if (platform === 'WEBHOOK') {
            return webhookConfig?.data?.[0]?.status === 'ENABLED' &&  webhookData?.data?.some((webhook: any) => webhook.status === 'ENABLED');
        }
        return false;
    }

    const getActualIntegratedLink = () => {
        if (platform === 'PIPEDRIVE' && integrationId) {
            return `${integratedLink}?id=${integrationId}`;
        }
        return integratedLink;
    };


    const [disconnectOpen, setDisconnectOpen] = useState(false);
    return (
        <div className='w-[317px] flex flex-col border-line border h-[250px]'>
            <div className="flex justify-between items-center px-4"><img className='my-6' src={image} alt={name} />
            {isIntegrationEnabled() &&
                <PhoneHQGlyph name='done' size={16} className='rounded-full text-white bg-success w-5 h-5'/>
                
            }
    
            </div>
            <div className=' px-4 pb-4'>
                <div className='font-medium'>{name}</div>
                <div className='text-xs text-ink-secondary'>{description}</div>
            </div>
            <div className={'flex  mt-auto border-t border-line ' + (isEnabled ? 'justify-between' : 'justify-end')}>
                {isEnabled && <div className={'flex items-center'}>
                    <PhoneHQButton variant="text-danger" size="sm" onClick={() => setDisconnectOpen(true)} className='m-4' phq-id={`integrationList-${platform}-disconnectFBtn`}>Disconnect</PhoneHQButton>
                    <DisconnectIntegrationDialog open={disconnectOpen} onOpenChange={setDisconnectOpen} integrationId={integrationId} platform={platform}/>
                </div>}
                <Link to={isEnabled ? getActualIntegratedLink() : link}>
                    <PhoneHQButton variant="text" size="sm" className='m-4' phq-id={`integrationList-${platform}-viewIntegrationFBtn`}>View integration</PhoneHQButton>
                </Link>
            </div>
        </div>
    );
}

export default function Integrations() {
    const {data: chatWebhooksEnabled} = useIsChatWebhooksEnabledQuery();
    const { data: integrationsData } = useGetIntegrationsQuery();
    const role = useSelector((state: any) => state.auth.role);

    const findIntegrationByPlatform = (platform: Platform) => {
        if (!integrationsData?.data) return null;

        const integrationsArray = Array.isArray(integrationsData.data)
            ? integrationsData.data
            : integrationsData.data.current
                ? [integrationsData.data.current]
                : [];

        return integrationsArray.find((integration: any) =>
            integration.platform === platform &&
            (!integration.details?.disabled)
        );
    };

    return (
        <div className='w-full'>
            {Boolean(role == 'ADMIN' || role == 'SUPERADMIN') &&

            <div className='h-[calc(100vh-130px)] overflow-y-auto'>
                <div className='ml-8'>
                </div>
                <div
                    className={`max-h-[calc(100vh-130px)] overflow-x-hidden overflow-y-scroll gap-8 flex flex-wrap ${mobileWidth || tabletWidth ? 'px-4' : 'p-6'}`}>
                    {integrations.filter(integration => integration.id !== 'INCOMING_CHAT_WEBHOOK' || chatWebhooksEnabled === true).map((integration, index) => {
                        const activeIntegration = findIntegrationByPlatform(integration.id as Platform);

                        return (
                            <IntegrationCard
                                integrationId={activeIntegration?.id}
                                isEnabled={!!activeIntegration}
                                platform={integration.id}
                                key={index}
                                {...integration}
                            />
                        );
                    })}
                </div>
            </div>
            }
            {
            Boolean(role == 'USER') &&
                <AdminPanelRestrictedView></AdminPanelRestrictedView>
            }
        </div>
    );
}